import { put, takeEvery, call, select } from "redux-saga/effects";
import { redirectToLoginPage } from "../utils/cognito-auth";
import { RootState } from "../redux/reducers/rootReducer";

// Watcher: listen to specific action and call worker
export function* watchHttpAction() {
  yield takeEvery("*", sendHttpRequest);
}

// Worker: send HTTP request
export function* sendHttpRequest(action: any): Generator<any, void, any> {
  if ("http" in action && "cognito" in action.http) {
    yield call(cognitoRequestSender, action);
  }
}

// Selectors to get user data from Redux - using direct access fields
const getUserData = (state: RootState) => ({
  token: state.user.token,
  userName: state.user.userName,
  email: state.user.email,
});

// Helper: Add common params (username, email) to request
function addCommonParams(effect: any, userName: string, email: string) {
  let url = effect.url;
  let jsonParams = effect.json || {};

  // Add username and email to query params for non-OPTIONS requests
  if (effect.method !== 'OPTIONS') {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}UserName=${userName}`;
    if (email) {
      url = `${url}&Email=${email}`;
    }
  }

  // Add username to JSON body for non-GET requests
  if (effect.method !== 'GET' && !Array.isArray(jsonParams)) {
    jsonParams = {
      ...jsonParams,
      UserName: userName,
    };
  }

  return { url, jsonParams };
}

// Send HTTP request with Cognito authorization
function* cognitoRequestSender(action: any): Generator<any, void, any> {
  const { effect, commit, rollback } = action.http.cognito;
  
  // Get token, username, and email from Redux store
  const { token, userName, email }: { token?: string; userName?: string; email?: string } = yield select(getUserData);

  // Verify if the session is still valid
  if (token && token !== "") {
    // Add common params (username, email) to request
    const { url, jsonParams } = addCommonParams(effect, userName || '', email || '');

    try {
      const response: Response = yield call(fetch, url, {
        method: effect.method,
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
          ...(effect.headers || {}),
        },
        body: effect.method !== 'GET' ? JSON.stringify(jsonParams) : null,
      });

      if (response.ok) {
        // Success
        const jsonResponse: string = yield call([response, 'text']);
        yield put({
          ...commit,
          statusCode: response.status,
          payload: JSON.parse(jsonResponse.replace(/\bNaN\b/g, "null")),
        });
      } else {
        // Failure
        const jsonResponse: string = yield call([response, 'text']);
        yield put({
          ...rollback,
          statusCode: response.status,
          payload: JSON.parse(jsonResponse),
        });
      }
    } catch (e: any) {
      console.error("[Saga] HTTP request error:", e);
      yield put(rollback);
    }
  } else {
    // Session expired, redirect to login
    console.log("[Saga] Session expired, redirecting to login");
    redirectToLoginPage();
  }
}
